import React, { useState } from 'react';
import { useBrand } from '../../../app/config/brand';
import { Icons } from '../../icons/Icons';

interface NewHeaderProps {
  onLogout: () => void;
  user?: { name?: string; email?: string; picture?: string };
}

export const NewHeader: React.FC<NewHeaderProps> = ({ onLogout, user }) => {
  const [accountOpen, setAccountOpen] = useState(false);
  const brand = useBrand();

  const getUserInitials = () => {
    if (user?.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return parts[0][0] + parts[parts.length - 1][0];
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return brand.user.firstName[0] + brand.user.lastName[0];
  };

  const displayName = user?.name || user?.email || brand.user.name;
  const displayEmail = user?.email || brand.user.email;

  return (
    <header
      className="bg-white border-b px-4 sm:px-6 py-3 flex items-center justify-between gap-3"
      style={{ borderColor: brand.colors.border }}
    >
      {/* Search Bar - hidden on mobile, shown on larger screens */}
      <div className="hidden sm:block flex-1 max-w-xl">
        <div className="relative">
          <Icons.Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
            style={{ color: brand.colors.textMuted }}
          />
          <label htmlFor="header-search" className="sr-only">Search</label>
          <input
            id="header-search"
            type="text"
            placeholder="Search e-file history or order ID..."
            className="w-full pl-10 pr-4 py-3 sm:py-2.5 rounded-lg border text-base sm:text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 min-h-touch"
            style={{
              borderColor: brand.colors.border,
              color: brand.colors.text,
            }}
          />
        </div>
      </div>

      {/* Mobile search button */}
      <button
        aria-label="Search"
        className="sm:hidden p-2 rounded-lg hover:bg-gray-50 min-h-touch min-w-touch flex items-center justify-center ml-auto"
      >
        <Icons.Search className="w-5 h-5" style={{ color: brand.colors.textMuted }} />
      </button>

      {/* Account Dropdown */}
      <div className="relative">
        <button
          onClick={() => setAccountOpen(!accountOpen)}
          className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors min-h-touch"
        >
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {getUserInitials()}
            </div>
          )}
          <span className="hidden sm:inline text-sm font-medium" style={{ color: brand.colors.text }}>
            {displayName}
          </span>
          <Icons.ChevronDown className="w-4 h-4" style={{ color: brand.colors.textLight }} />
        </button>

        {accountOpen && (
          <div
            className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg py-2 w-56 z-50 border"
            style={{ borderColor: brand.colors.border }}
          >
            <div className="px-4 py-3 border-b" style={{ borderColor: brand.colors.border }}>
              <div className="font-semibold" style={{ color: brand.colors.secondary }}>{displayName}</div>
              <div className="text-sm" style={{ color: brand.colors.textLight }}>{displayEmail}</div>
            </div>
            <button className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 min-h-touch">My Account</button>
            <button className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 min-h-touch">Downloads</button>
            <button className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 min-h-touch">Billing Center</button>
            <button
              onClick={onLogout}
              className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 min-h-touch"
              style={{ color: brand.colors.error }}
            >
              Log out
            </button>
          </div>
        )}
      </div>
    </header>
  );
};
